import { Code2, Columns2, PenLine } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export type EditorViewMode = 'write' | 'live' | 'split';

interface EditorViewToggleProps {
  value: EditorViewMode;
  onChange: (mode: EditorViewMode) => void;
}

/**
 * Three-way view switcher shared by `DjotEditor` and `AsciiDocEditor`:
 *
 *  - **Raw edit** (`write`): just the plain-text editor, full width —
 *    for focused writing without any styling pulling attention.
 *  - **Live edit** (`live`): just the styled, directly-editable
 *    `LiveEditor` surface, full width — type directly into text that
 *    already looks like the rendered result (see `LiveEditor.tsx`).
 *  - **Split editor** (`split`): raw text and the live editor side by
 *    side in a resizable split — the editor's original default, useful
 *    for seeing exactly how each edit affects the styled result as you
 *    type in either pane.
 *
 * Available at every viewport width; callers pick a sensible initial
 * default (e.g. `write` on narrow screens, `split` on wide ones) but the
 * writer can always switch to any of the three.
 */
export function EditorViewToggle({ value, onChange }: EditorViewToggleProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v as EditorViewMode)}
      variant="outline"
      size="sm"
      className="shrink-0"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleGroupItem value="write" aria-label="Raw edit view">
            <Code2 className="size-4" />
          </ToggleGroupItem>
        </TooltipTrigger>
        <TooltipContent>Raw edit</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleGroupItem value="live" aria-label="Live edit view">
            <PenLine className="size-4" />
          </ToggleGroupItem>
        </TooltipTrigger>
        <TooltipContent>Live edit</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleGroupItem value="split" aria-label="Split editor view">
            <Columns2 className="size-4" />
          </ToggleGroupItem>
        </TooltipTrigger>
        <TooltipContent>Split editor</TooltipContent>
      </Tooltip>
    </ToggleGroup>
  );
}
